import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import AddButton from "../../../../components/AddButton";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import {
  DoorBuzzerAddState,
  DoorBuzzerSaving,
  SecurityAddState,
  SecurityEditState,
  DoorBuzzerModalState,
  DoorBuzzerForEdit,
  dbDeactivateModal,
} from "../atoms/atoms";
import { IDoorBuzzer, ISecurityDetails } from "../types";
import Modal from "../../../../components/Modal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import MinusButton from "../../../../components/MinusButton";
import PlusButton from "../../../../components/PlusButton";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import { useWarrantyCoverages } from "../hooks/useWarrantyCoverages";
import { useVendors } from "../hooks/useVendors";
import { addSingleDoorBuzzer } from "../../../../actions/CarrierAgreementMgmt/Security";
import useAddSingleDoorBuzzer from "../hooks/useAddSingleDoorBuzzer";
import { toast } from "../../../../components/toastNotification";
import { useGetDoorBuzzersByRecordNumber } from "../hooks/useGetDoorBuzzersByRecordNumber";
import DoorBuzzerCard from "../../../../components/DoorBuzzerCard";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import useEditSingleDoorBuzzer from "../hooks/useEditSingleDoorBuzzer";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import useDeleteDoorBuzzer from "../hooks/useDeleteDoorBuzzer";
import CloseIconButton from "../../../../components/CloseIconButton";
import { useSelectedNumberOfButtons } from "../hooks/useSelectedNumberOfButtons";
import SecurityModal from "../../../../components/SecurityModal";

interface DoorBuzzerProps {
  doorBuzzers?: Array<IDoorBuzzer>;
  rowForEdit: ISecurityDetails | undefined;
}
const defaultDoorBuzzer = [
  {
    parentRow: true,
    tableName: "doorBuzzer",
  },
] as Array<IDoorBuzzer>;

const DoorBuzzerForm = ({
  rowForEdit,
  doorBuzzers = defaultDoorBuzzer,
}: DoorBuzzerProps): JSX.Element => {
  const [isShowDoorBuzzerModal, setShowDoorBuzzerModal] =
    useRecoilState(DoorBuzzerAddState);
  const [showDoorBuzzerCardModal, setShowDoorBuzzerCardModal] =
    useRecoilState(DoorBuzzerModalState);
  const [isAddState] = useRecoilState(SecurityAddState);
  const [isEditState] = useRecoilState(SecurityEditState);
  const [isAddInEditState, setIsAddInEditState] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(DoorBuzzerSaving);
  const [showDeactivateModal, setShowDeactivateModal] =
    useRecoilState(dbDeactivateModal);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [forDeleteDetails, setForDeleteDetails] = useState();
  const queryClient = useQueryClient();
  const [hasFirstFieldValue, setHasFirstFieldValue] = useState(true);
  const [clickCount, setClickCount] = useState(0);
  const [doorBuzzerForEditData, setDoorBuzzerForEditData] =
    useRecoilState<any>(DoorBuzzerForEdit);

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const deletePermission = PERMISSIONS.permission_security_delete;

  //#region Form
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const { data: numberOfButtons } = useSelectedNumberOfButtons();
  const numberOfButtonOption = numberOfButtons?.data?.map(
    (x: { id: number; numberOfbutton: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.numberOfbutton,
      };
    }
  );

  const formMethods = useFormContext();
  //#endregion
  //#region Add/minus of forms
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "doorBuzzer",
  });

  const tableArray =
    doorBuzzers.length > 0 && watch(`${doorBuzzers[0].tableName}`);

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (doorBuzzers) {
      replace(doorBuzzers);
    }
  }, [doorBuzzers]);
  //#endregion Add/minus
  //#region Options

  const yesOrNoOption = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];
  const { data: WarrantyCoverages } = useWarrantyCoverages();
  const warrantyCoveragesOption = WarrantyCoverages?.data?.map(
    (x: { id: number; warrantyCoverage: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.warrantyCoverage,
      };
    }
  );
  const { data: Vendors } = useVendors();
  const vendorsDoorBuzzerOption = Vendors?.data
    ?.filter((b) => b.vendorType === 1 || b.vendorType === 4)
    ?.map(
      (x: {
        id: number;
        name: string;
        contact: string;
        email: string;
        city: string;
        accountNumber: string;
        state: string;
        street: string;
        vendorType: number;
        zipcode: string;
        phone: string;
        tonerRequestEmail: string;
        tonerRequestPhone: string;
      }) => {
        return {
          id: x.id,
          value: x.id,
          label: x.name,
          email: x.email,
          accountNumber: x.accountNumber,
          address:
            (x.street ? x.street : "") +
            " " +
            (x.city ? x.city : "") +
            " " +
            (x.state ? x.state : "") +
            " " +
            (x.zipcode ? x.zipcode : ""),
          vendorType: x.vendorType,
          vendorPhone: x.phone,
          trEmail: x.tonerRequestEmail,
          tonerPhone: x.tonerRequestPhone,
          contact: x.contact,
        };
      }
    );
  //#endregion
  //Add Validation
  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("doorBuzzer[0].siteNumber")
        ? setHasFirstFieldValue(true)
        : setHasFirstFieldValue(false);
    }
    if (buttonState === "proceed") {
      const fieldLen = controlledFields.length;
      let hasValueCount = 0;

      controlledFields.forEach((obj: IDoorBuzzer, i: number) =>
        Object.entries(obj)?.forEach(([key, value]) => {
          if (key === "siteNumber") {
            if (value) {
              hasValueCount = hasValueCount + 1;
              if (fieldLen === hasValueCount) {
                setShowDoorBuzzerModal(false);
              }
            } else {
              setClickCount(clickCount + 1);
              toast.fire(
                "Information",
                "Please fill the Site Number to proceed.",
                "info"
              );
              setHasFirstFieldValue(false);
            }
          }
        })
      );
    }
    if (buttonState === "saveEdit") {
      setClickCount(clickCount + 1);
      watch("siteNumber")
        ? handleEditSave()
        : toast.fire(
            "Information",
            "Please fill the Site Number to save.",
            "info"
          );
    }
  };

  useEffect(() => {
    !hasFirstFieldValue &&
      toast.fire(
        "Information",
        "Please fill the Site Number to add new.",
        "info"
      );
  }, [clickCount]);
  //Single Add Initialization
  const {
    mutate: addSingleDoorBuzzer,
    isSuccess: addDoorBuzzerSuccess,
    data: addDoorBuzzerResult,
    isError: addDoorBuzzerError,
    reset: resetAddDoorBuzzerData,
  } = useAddSingleDoorBuzzer();
  //Single Edit Initialization
  const {
    mutate: editSingleDoorBuzzer,
    isSuccess: editDoorBuzzerSuccess,
    data: editDoorBuzzerResult,
    isError: editDoorBuzzerError,
    reset: resetEditDoorBuzzerData,
  } = useEditSingleDoorBuzzer();
  //Delete Initialization
  const {
    mutate: deleteDoorBuzzer,
    isSuccess: deleteDoorBuzzerSuccess,
    data: deleteDoorBuzzerResult,
    isError: deleteDoorBuzzerError,
    reset: resetDeleteDoorBuzzerData,
  } = useDeleteDoorBuzzer();

  //Reset form data sing Add
  const resetData = () => {
    formMethods.setValue("doorBuzzer[0].siteNumber", "");
    formMethods.setValue("doorBuzzer[0].connection", "");
    formMethods.setValue("doorBuzzer[0].dbinstallDate", "");
    formMethods.setValue("doorBuzzer[0].type", "");
    formMethods.setValue("doorBuzzer[0].numberOfButtonSelectedId", "");
    formMethods.setValue("doorBuzzer[0].warranty", "");
    formMethods.setValue("doorBuzzer[0].warrantyLength", "");
    formMethods.setValue("doorBuzzer[0].warrantyCoverageId", "");
    formMethods.setValue("doorBuzzer[0].dbinstallVendorId", "");
    formMethods.setValue("doorBuzzer[0].dbwarrantyVendorId", "");
    formMethods.setValue("doorBuzzer[0].dbservicingVendorId", "");
  };
  //Save Get values for single add
  const handleAddSave = () => {
    setIsSaving(true);
    const doorBuzzerData = {
      recordNumber: rowForEdit?.recordNumber,
      siteNumber: formMethods.getValues("doorBuzzer[0].siteNumber"),
      connection: formMethods.getValues("doorBuzzer[0].connection"),
      dbinstallDate: formMethods.getValues("doorBuzzer[0].dbinstallDate"),
      type: formMethods.getValues("doorBuzzer[0].type"),
      numberOfButtonSelectedId: formMethods.getValues(
        "doorBuzzer[0].numberOfButtonSelectedId"
      ),
      warranty: formMethods.getValues("doorBuzzer[0].warranty"),
      warrantyLength: formMethods.getValues("doorBuzzer[0].warrantyLength"),
      warrantyCoverageId: formMethods.getValues(
        "doorBuzzer[0].warrantyCoverageId"
      ),
      dbinstallVendorId: formMethods.getValues(
        "doorBuzzer[0].dbinstallVendorId"
      ),
      dbwarrantyVendorId: formMethods.getValues(
        "doorBuzzer[0].dbwarrantyVendorId"
      ),
      dbservicingVendorId: formMethods.getValues(
        "doorBuzzer[0].dbservicingVendorId"
      ),
    };

    addSingleDoorBuzzer(doorBuzzerData);
    setShowDoorBuzzerModal(false);
  };
  //Set Edit default values
  const setEditDoorBuzzerValues = (doorBuzzer) => {
    formMethods.setValue("id", doorBuzzer.id);
    formMethods.setValue("siteNumber", doorBuzzer.siteNumber);
    formMethods.setValue("connection", doorBuzzer.connection);
    formMethods.setValue(
      "dbinstallDate",
      doorBuzzer.dbinstallDate ? doorBuzzer.dbinstallDate?.slice(0, 10) : null
    );
    formMethods.setValue("type", doorBuzzer.type);
    formMethods.setValue(
      "numberOfButtonSelectedId",
      doorBuzzer.numberOfButtonSelectedId
    );
    formMethods.setValue("warranty", doorBuzzer.warranty);
    formMethods.setValue("warrantyLength", doorBuzzer.warrantyLength);
    formMethods.setValue("warrantyCoverageId", doorBuzzer.warrantyCoverageId);
    formMethods.setValue("dbinstallVendorId", doorBuzzer.dbinstallVendorId);
    formMethods.setValue("dbwarrantyVendorId", doorBuzzer.dbwarrantyVendorId);
    formMethods.setValue("dbservicingVendorId", doorBuzzer.dbservicingVendorId);
    formMethods.setValue("isActive", doorBuzzer.isActive);
    formMethods.setValue("createdBy", doorBuzzer.createdBy);
    formMethods.setValue("createdDate", doorBuzzer.createdDate);

    queryClient.invalidateQueries("GetDoorBuzzersByRecordNumber");
    setShowDoorBuzzerCardModal(true);
  };
  //Save Edit single door buzzer
  const handleEditSave = () => {
    setIsSaving(true);
    const doorBuzzerNewData = {
      recordNumber: rowForEdit?.recordNumber,
      id: formMethods.getValues("id"),
      siteNumber: formMethods.getValues("siteNumber"),
      connection: formMethods.getValues("connection"),
      dbinstallDate: formMethods.getValues("dbinstallDate")
        ? formMethods.getValues("dbinstallDate").slice(0, 10)
        : null,
      type: formMethods.getValues("type"),
      numberOfButtonSelectedId: formMethods.getValues(
        "numberOfButtonSelectedId"
      ),
      warranty: formMethods.getValues("warranty"),
      warrantyLength: formMethods.getValues("warrantyLength"),
      warrantyCoverageId: formMethods.getValues("warrantyCoverageId"),
      dbinstallVendorId: formMethods.getValues("dbinstallVendorId"),
      dbwarrantyVendorId: formMethods.getValues("dbwarrantyVendorId"),
      dbservicingVendorId: formMethods.getValues("dbservicingVendorId"),
      isActive: formMethods.getValues("isActive"),
      createdBy: formMethods.getValues("createdBy"),
      createdDate: formMethods.getValues("createdDate"),
    };
    const doorBuzzerOldData = {
      recordNumber: doorBuzzerForEditData?.recordNumber,
      id: doorBuzzerForEditData?.id,
      siteNumber: doorBuzzerForEditData?.siteNumber,
      connection: doorBuzzerForEditData?.connection,
      dbinstallDate: doorBuzzerForEditData?.dbinstallDate
        ? doorBuzzerForEditData?.dbinstallDate?.slice(0, 10)
        : null,
      type: doorBuzzerForEditData?.type,
      numberOfButtonSelectedId: doorBuzzerForEditData?.numberOfButtonSelectedId,
      warranty: doorBuzzerForEditData?.warranty,
      warrantyLength: doorBuzzerForEditData?.warrantyLength,
      warrantyCoverageId: doorBuzzerForEditData?.warrantyCoverageId,
      dbinstallVendorId: doorBuzzerForEditData?.dbinstallVendorId,
      dbwarrantyVendorId: doorBuzzerForEditData?.dbwarrantyVendorId,
      dbservicingVendorId: doorBuzzerForEditData?.dbservicingVendorId,
      isActive: doorBuzzerForEditData?.isActive,
      createdBy: doorBuzzerForEditData?.createdBy,
      createdDate: doorBuzzerForEditData?.createdDate,
    };
    const newData = JSON.stringify(doorBuzzerNewData);
    const oldData = JSON.stringify(doorBuzzerOldData);
    if (newData === oldData) {
      toast.fire("Saving Failed", "No changes has been made.", "warning");
      setIsSaving(false);
    } else {
      setIsSaving(true);
      editSingleDoorBuzzer(doorBuzzerNewData);
      setShowDoorBuzzerCardModal(false);
    }
  };
  const isVendorRequired = formMethods.watch("typeOfVendorId") > 0;

  //Toasters
  if (addDoorBuzzerSuccess && addDoorBuzzerResult) {
    if (
      addDoorBuzzerResult.status === 201 ||
      addDoorBuzzerResult.status === 200
    ) {
      toast.fire("Success", "Door buzzer added successfully.", "success");
      queryClient.invalidateQueries("GetDoorBuzzersByRecordNumber");
    }
    setIsSaving(false);
    resetAddDoorBuzzerData();
    resetData();
  }
  if (editDoorBuzzerSuccess && editDoorBuzzerResult) {
    if (
      editDoorBuzzerResult.status === 201 ||
      editDoorBuzzerResult.status === 200
    ) {
      toast.fire("Success", "Door buzzer updated successfully.", "success");
      queryClient.invalidateQueries("GetDoorBuzzersByRecordNumber");
    }
    setIsSaving(false);
    resetEditDoorBuzzerData();
  }
  if (deleteDoorBuzzerSuccess && deleteDoorBuzzerResult) {
    if (
      deleteDoorBuzzerResult.status === 201 ||
      deleteDoorBuzzerResult.status === 200
    ) {
      toast.fire("Success", "Door buzzer deactivated successfully.", "success");
      queryClient.invalidateQueries("GetDoorBuzzersByRecordNumber");
    }
    resetDeleteDoorBuzzerData();
  }
  //Deactivate
  const handleDeactivate = () => {
    deleteDoorBuzzer(forDeleteDetails);
    setShowDeactivateModal(false);
  };
  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate this Door Buzzer?"}
    </div>
  );
  useEffect(() => {
    if (userPermissions.includes(deletePermission)) {
      setHasDeletePermission(true);
    }
  }, []);
  const { data: doorBuzzersData } = useGetDoorBuzzersByRecordNumber(
    rowForEdit ? rowForEdit?.recordNumber : 0
  );

  return (
    <div className="mx-2 mt-2 bg-white box-shadow rounded-lg h-full">
      <div className="flex flex-row justify-between py-2">
        <div className="text-quinary font-bold font-sans px-4">
          Door Buzzer Section
        </div>
        {userPermissions.includes(addPermission) ? (
          <div className="py-2 px-4">
            <AddButton
              className={"opacity-100"}
              disabled={
                doorBuzzersData !== undefined
                  ? doorBuzzersData.length >= 5
                    ? true
                    : false
                  : false
              }
              buttonText="Add Door Buzzer"
              onClick={() => {
                setShowDoorBuzzerModal(true);
                setIsAddInEditState(true);
              }}
            />
          </div>
        ) : (
          <div className="py-5 px-4"></div>
        )}
      </div>
      {isAddState && (
        <div className="text-quinary font-bold font-sans py-2 px-4">
          <h1
            className="mr-auto truncate w-full cursor-pointer text-ellipsis font-bold text-primary underline text-lg uppercase"
            onClick={() => {
              setShowDoorBuzzerModal(true);
            }}
          >
            {controlledFields &&
              controlledFields[0]?.siteNumber &&
              "View Door Buzzer/s"}
          </h1>
        </div>
      )}
      {/* Show card edit button if edit state*/}
      {isEditState && (
        <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
          <div className="grid gap-2 grid-cols-5">
            {/* Card */}
            {doorBuzzersData?.map((doorBuzzer) => (
              <div key={doorBuzzer.id}>
                <DoorBuzzerCard
                  doorBuzzer={doorBuzzer}
                  isEditState={isEditState}
                  onClickView={() => {}}
                  onClickEdit={() => {
                    setEditDoorBuzzerValues(doorBuzzer);
                    setDoorBuzzerForEditData(doorBuzzer);
                  }}
                  onClickDelete={() => {
                    setShowDeactivateModal(true);
                    setForDeleteDetails(doorBuzzer);
                  }}
                  hasDeletePermission={hasDeletePermission}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Add Door Buzzer Form */}
      {isShowDoorBuzzerModal && (
        <SecurityModal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={isShowDoorBuzzerModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState || isAddInEditState
                ? "Add Door Buzzer"
                : "Edit Door Buzzer"}
            </div>
            {isAddState ? (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={"Proceed"}
                  className={undefined}
                  onClick={() => {
                    checkEmptyFields("proceed");
                    setHasFirstFieldValue(true);
                  }}
                />
                {/* <CancelButton
                  onClick={() => {
                    setShowDoorBuzzerModal(false);
                    resetData();
                    remove([1, 2, 3, 4]);
                  }}
                /> */}
                <div className="relative -top-2 -right-6">
                  <CloseIconButton
                    onClick={() => setShowDoorBuzzerModal(false)}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={() => {
                    watch("doorBuzzer[0].siteNumber")
                      ? handleAddSave()
                      : toast.fire(
                          "Information",
                          "Please fill the site number field to save.",
                          "info"
                        );
                  }}
                />
                <CancelButton
                  onClick={() => {
                    setShowDoorBuzzerModal(false);
                    resetData();
                  }}
                />
              </div>
            )}
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="text-xs text-secondary italic font-normal m-2">
            <b> Note:</b> All fields with * are required to fill.
          </div>
          {/* <div
            className="w-full borderContainer bg-white p-2"
            style={
              controlledFields.length > 1
                ? {
                    width: "auto",
                    height: "80vh",
                    overflow: "auto",
                  }
               : undefined
            }
          > */}
          <div className="box-shadow rounded-md modal-content">
            {controlledFields &&
              controlledFields.map((field, index) => {
                return (
                  <>
                    <div className="ml-4 py-2" key={field.id}>
                      {index > 0 ? (
                        <MinusButton
                          type="button"
                          onClick={() => remove(index)}
                        />
                      ) : (
                        !isEditState && (
                          <PlusButton
                            type="button"
                            onClick={(e) => {
                              if (controlledFields.length < 5) {
                                checkEmptyFields("add");
                                watch(`doorBuzzer[0].siteNumber`) &&
                                  append({
                                    tableName: doorBuzzers[0]?.tableName,
                                    parentRow: false,
                                  });
                              }
                            }}
                            className={
                              controlledFields.length === 5
                                ? "opacity-50 hover:bg-primary"
                                : ""
                            }
                          />
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
                      <div className="space-y-1">
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].siteNumber`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <TextInputLabelHookForm
                              name={`${doorBuzzers[0].tableName}[${index}].siteNumber`}
                              label="Site Number"
                              type="text"
                              register={formMethods.register(
                                `${doorBuzzers[0].tableName}[${index}].siteNumber`,
                                {}
                              )}
                              maxLength={50}
                              className={
                                formMethods.formState.errors.doorId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={true}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${doorBuzzers[0].tableName}[${index}].connection`}
                          label="Connection"
                          type="text"
                          register={formMethods.register(
                            `${doorBuzzers[0].tableName}[${index}].connection`,
                            {}
                          )}
                          maxLength={50}
                        />
                        <TextInputLabelHookForm
                          name={`${doorBuzzers[0].tableName}[${index}].dbinstallDate`}
                          label="Install Date"
                          type="date"
                          register={formMethods.register(
                            `${doorBuzzers[0].tableName}[${index}].dbinstallDate`,
                            {}
                          )}
                        />
                        {/* For Clarification */}
                        <TextInputLabelHookForm
                          name={`${doorBuzzers[0].tableName}[${index}].type`}
                          label="Type"
                          type="text"
                          register={formMethods.register(
                            `${doorBuzzers[0].tableName}[${index}].type`,
                            {}
                          )}
                          maxLength={255}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].numberOfButtonSelectedId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={numberOfButtonOption}
                              label="# of Button Select list"
                              className={
                                formMethods.formState.errors
                                  .numberOfButtonSelectedId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].warranty`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={yesOrNoOption}
                              label="Warranty"
                              className={
                                formMethods.formState.errors.warranty &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                      </div>
                      <div className="space-y-1">
                        {/* For Clarification */}
                        <TextInputLabelHookForm
                          name={`${doorBuzzers[0].tableName}[${index}].warrantyLength`}
                          label="Warranty Length"
                          type="text"
                          register={formMethods.register(
                            `${doorBuzzers[0].tableName}[${index}].warrantyLength`,
                            {}
                          )}
                          maxLength={10}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].warrantyCoverageId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={warrantyCoveragesOption}
                              label="Warranty Coverage"
                              className={
                                formMethods.formState.errors
                                  .warrantyCoverageId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].dbinstallVendorId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                                formMethods.setValue(
                                  "dbinstallVendorAddress",
                                  e ? e.address : null
                                );
                                formMethods.setValue(
                                  "dbinstallVendorPhone",
                                  e ? e.vendorPhone : null
                                );
                                formMethods.setValue(
                                  "contact",
                                  e ? e.contact : null
                                );
                              }}
                              value={field.value}
                              options={vendorsDoorBuzzerOption}
                              label="Install Vendor"
                              className={
                                formMethods.formState.errors
                                  .dbinstallVendorId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].dbwarrantyVendorId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={vendorsDoorBuzzerOption}
                              label="Warranty Vendor"
                              className={
                                formMethods.formState.errors
                                  .dbwarrantyVendorId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <Controller
                          name={`${doorBuzzers[0].tableName}[${index}].dbservicingVendorId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={vendorsDoorBuzzerOption}
                              label="Servicing Vendor"
                              className={
                                formMethods.formState.errors
                                  .dbservicingVendorId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />

                        {isVendorRequired && (
                          <>
                            <TextInputLabelHookForm
                              name={`${doorBuzzers[0].tableName}[${index}].dbinstallVendorAddress`}
                              label="Vendor Address"
                              type="text"
                              required={true}
                              className={
                                formMethods.formState.errors
                                  .dbinstallVendorAddress &&
                                formMethods.formState.errors
                                  .dbinstallVendorAddress.message &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              register={formMethods.register(
                                `${doorBuzzers[0].tableName}[${index}].dbinstallVendorAddress`,
                                {
                                  required: {
                                    value: true,
                                    message: "Required Field",
                                  },
                                }
                              )}
                              disabled={true}
                              maxLength={155}
                            />
                            <TextInputLabelHookForm
                              name={`${doorBuzzers[0].tableName}[${index}].dbinstallVendorPhone`}
                              label="Vendor Phone"
                              type="text"
                              required={true}
                              className={
                                formMethods.formState.errors
                                  .dbinstallVendorPhone &&
                                formMethods.formState.errors
                                  .dbinstallVendorPhone.message &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              register={formMethods.register(
                                `${doorBuzzers[0].tableName}[${index}].dbinstallVendorPhone`,
                                {
                                  required: {
                                    value: true,
                                    message: "Required Field",
                                  },
                                }
                              )}
                              disabled={true}
                              maxLength={50}
                            />
                            <TextInputLabelHookForm
                              name={`${doorBuzzers[0].tableName}[${index}].dbinstallVendorContact`}
                              label="Contact"
                              type="text"
                              required={true}
                              className={
                                formMethods.formState.errors.contact &&
                                formMethods.formState.errors.contact.message &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              register={formMethods.register(
                                `${doorBuzzers[0].tableName}[${index}].dbinstallVendorContact`,
                                {
                                  required: {
                                    value: true,
                                    message: "Required Field",
                                  },
                                }
                              )}
                              disabled={true}
                              maxLength={50}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="w-full border-b-2 border-grey-200"></div>
                    </div>
                  </>
                );
              })}
          </div>
        </SecurityModal>
      )}
      {/* Edit Door Buzzer Card Form */}
      {showDoorBuzzerCardModal && (
        <Modal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={showDoorBuzzerCardModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState ? "Add Door Buzzer" : "Edit Door Buzzer"}
            </div>
            <div className="flex justify-between gap-2 mr-2">
              <SubmitHookForm
                type="button"
                disabled={false}
                buttonText={isSaving ? "Saving.." : "Save"}
                className={undefined}
                onClick={() => {
                  checkEmptyFields("saveEdit");
                }}
              />
              <CancelButton
                onClick={() => {
                  setShowDoorBuzzerCardModal(false);
                }}
              />
            </div>
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
            <div className="space-y-1">
              <Controller
                name="siteId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextInputLabelHookForm
                    name="siteNumber"
                    label="Site Number"
                    type="text"
                    register={formMethods.register("siteNumber", {})}
                    maxLength={50}
                    className={
                      formMethods.formState.errors.siteId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="connection"
                label="Connection"
                type="text"
                register={formMethods.register("connection", {})}
                maxLength={50}
              />
              <TextInputLabelHookForm
                name="dbinstallDate"
                label="Install Date"
                type="date"
                register={formMethods.register("dbinstallDate", {})}
              />
              {/* For Clarification */}
              <TextInputLabelHookForm
                name="type"
                label="Type"
                type="text"
                register={formMethods.register("type", {})}
                maxLength={255}
              />
              <Controller
                name="numberOfButtonSelectedId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={numberOfButtonOption}
                    label="# of Button Select list"
                    className={
                      formMethods.formState.errors.numberOfButtonSelectedId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="warranty"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={yesOrNoOption}
                    label="Warranty"
                    className={
                      formMethods.formState.errors.warranty &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
            </div>
            <div className="space-y-1">
              {/* For Clarification */}
              <TextInputLabelHookForm
                name="warrantyLength"
                label="Warranty Length"
                type="text"
                register={formMethods.register("warrantyLength", {})}
                maxLength={10}
              />
              <Controller
                name="warrantyCoverageId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={warrantyCoveragesOption}
                    label="Warranty Coverage"
                    className={
                      formMethods.formState.errors.warrantyCoverageId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="dbinstallVendorId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      formMethods.setValue(
                        "dbinstallVendorAddress",
                        e ? e.address : null
                      );
                      formMethods.setValue(
                        "dbinstallVendorPhone",
                        e ? e.vendorPhone : null
                      );
                      formMethods.setValue("contact", e ? e.contact : null);
                    }}
                    value={field.value}
                    options={vendorsDoorBuzzerOption}
                    label="Install Vendor"
                    className={
                      formMethods.formState.errors.dbinstallVendorId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="dbwarrantyVendorId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={vendorsDoorBuzzerOption}
                    label="Warranty Vendor"
                    className={
                      formMethods.formState.errors.dbwarrantyVendorId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <Controller
                name="dbservicingVendorId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={vendorsDoorBuzzerOption}
                    label="Servicing Vendor"
                    className={
                      formMethods.formState.errors.dbservicingVendorId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />

              {isVendorRequired && (
                <>
                  <TextInputLabelHookForm
                    name="dbinstallVendorAddress"
                    label="Vendor Address"
                    type="text"
                    required={true}
                    className={
                      formMethods.formState.errors.dbinstallVendorAddress &&
                      formMethods.formState.errors.dbinstallVendorAddress
                        .message &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    register={formMethods.register("dbinstallVendorAddress", {
                      required: {
                        value: true,
                        message: "Required Field",
                      },
                    })}
                    disabled={true}
                    maxLength={155}
                  />
                  <TextInputLabelHookForm
                    name="dbinstallVendorPhone"
                    label="Vendor Phone"
                    type="text"
                    required={true}
                    className={
                      formMethods.formState.errors.dbinstallVendorPhone &&
                      formMethods.formState.errors.dbinstallVendorPhone
                        .message &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    register={formMethods.register("dbinstallVendorPhone", {
                      required: {
                        value: true,
                        message: "Required Field",
                      },
                    })}
                    disabled={true}
                    maxLength={50}
                  />
                  <TextInputLabelHookForm
                    name="contact"
                    label="Contact"
                    type="text"
                    required={true}
                    className={
                      formMethods.formState.errors.contact &&
                      formMethods.formState.errors.contact.message &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    register={formMethods.register("contact", {
                      required: {
                        value: true,
                        message: "Required Field",
                      },
                    })}
                    disabled={true}
                    maxLength={50}
                  />
                </>
              )}
              {isEditState && hasDeletePermission && (
                <CheckBoxLabelHookForm
                  name="isActive"
                  type="checkbox"
                  register={register}
                  label="Active Account"
                  className="cursor-pointer auto-rows-auto text-right justify-items-center"
                />
              )}
            </div>
          </div>
        </Modal>
      )}
      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmBoxDelete
          ModalTitle="Security Door Buzzer"
          qConfirmation={deleteMessage}
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onClickFunction={() => handleDeactivate()}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};

export default DoorBuzzerForm;
